import { storage } from "@neutralinojs/lib";
import { isDesktop } from "../hooks/useIsDesktop";

/**
 * Persistent storage abstraction that uses:
 * - Neutralino.storage for desktop mode (persists between app sessions)
 * - localStorage for web mode (browser standard)
 */

async function waitForNeutralino(): Promise<void> {
  if (!isDesktop()) return;

  // Check if we're in fake desktop mode (development override)
  const isFakeDesktop =
    import.meta.env.VITE_FORCE_DESKTOP_MODE === "true" &&
    !Object.keys(window).some((key) => key.startsWith("NL_"));

  if (isFakeDesktop) {
    console.warn(
      "Running in fake desktop mode - Neutralino APIs not available",
    );
    return; // Don't wait for Neutralino in fake mode
  }

  // Wait for Neutralino to be ready with timeout
  return new Promise((resolve, reject) => {
    if (window.__NEUTRALINO_READY__) {
      resolve();
      return;
    }

    let attempts = 0;
    const maxAttempts = 200; // 10 seconds (200 * 50ms)

    const checkReady = () => {
      if (window.__NEUTRALINO_READY__) {
        resolve();
      } else if (attempts >= maxAttempts) {
        reject(new Error("Timeout waiting for Neutralino to be ready"));
      } else {
        attempts++;
        setTimeout(checkReady, 50);
      }
    };

    checkReady();
  });
}

export async function getItem(key: string): Promise<string | null> {
  if (isDesktop()) {
    // Check if we're in fake desktop mode
    const isFakeDesktop =
      import.meta.env.VITE_FORCE_DESKTOP_MODE === "true" &&
      !Object.keys(window).some((k) => k.startsWith("NL_"));

    if (isFakeDesktop) {
      console.warn(
        "Fake desktop mode: using localStorage instead of Neutralino storage",
      );
      return localStorage.getItem(key);
    }

    try {
      await waitForNeutralino();
      const data = await storage.getData(key);
      return data;
    } catch (error) {
      console.warn(
        `Failed to get data from Neutralino storage for key "${key}":`,
        error,
      );
      return null;
    }
  } else {
    return localStorage.getItem(key);
  }
}

export async function setItem(key: string, value: string): Promise<void> {
  if (isDesktop()) {
    // Check if we're in fake desktop mode
    const isFakeDesktop =
      import.meta.env.VITE_FORCE_DESKTOP_MODE === "true" &&
      !Object.keys(window).some((k) => k.startsWith("NL_"));

    if (isFakeDesktop) {
      console.warn(
        "Fake desktop mode: using localStorage instead of Neutralino storage",
      );
      localStorage.setItem(key, value);
      return;
    }

    try {
      await waitForNeutralino();
      await storage.setData(key, value);
    } catch (error) {
      console.error(
        `Failed to set data in Neutralino storage for key "${key}":`,
        error,
      );
      throw error;
    }
  } else {
    localStorage.setItem(key, value);
  }
}

export async function removeItem(key: string): Promise<void> {
  if (isDesktop()) {
    // Check if we're in fake desktop mode
    const isFakeDesktop =
      import.meta.env.VITE_FORCE_DESKTOP_MODE === "true" &&
      !Object.keys(window).some((k) => k.startsWith("NL_"));

    if (isFakeDesktop) {
      console.warn(
        "Fake desktop mode: using localStorage instead of Neutralino storage",
      );
      localStorage.removeItem(key);
      return;
    }

    try {
      await waitForNeutralino();
      // Setting null/undefined removes the item in Neutralino storage
      // @ts-expect-error - Neutralino storage.setData accepts null to remove items
      await storage.setData(key, null);
    } catch (error) {
      console.error(
        `Failed to remove data from Neutralino storage for key "${key}":`,
        error,
      );
      throw error;
    }
  } else {
    localStorage.removeItem(key);
  }
}

export async function clear(): Promise<void> {
  if (isDesktop()) {
    try {
      await waitForNeutralino();
      const keys = await storage.getKeys();
      await Promise.all(keys.map((key) => removeItem(key)));
    } catch (error) {
      console.error("Failed to clear Neutralino storage:", error);
      throw error;
    }
  } else {
    localStorage.clear();
  }
}

export async function getKeys(): Promise<string[]> {
  if (isDesktop()) {
    try {
      await waitForNeutralino();
      return await storage.getKeys();
    } catch (error) {
      console.warn("Failed to get keys from Neutralino storage:", error);
      return [];
    }
  } else {
    return Object.keys(localStorage);
  }
}
